import { PackageSearch } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import ImageCarousel from "../../components/AdminSideComponents/common/ImageCarousel";
import PageHeader from "../../components/AdminSideComponents/common/PageHeader";
import Pagination from "../../components/AdminSideComponents/common/Pagination";
import api from "../../configs/api";
import useDebounce from "../../Hooks/useDebounce";
import { useEntityModal } from "../../Hooks/useEntityModal";
import { confirmAction, showError, showSuccess } from "../../utils/toastUtils";
import EditProjectModal from "../../components/AdminSideComponents/projects/EditProjectModal";

/*  Project card  */
const ProjectCard = ({ project, onEdit, onDelete }) => {
  const navigate = useNavigate();

  return (
    <>
      <div
        className="group border border-borderLight/60 bg-surfaceColor overflow-hidden relative flex flex-col
hover:shadow-[0_12px_28px_rgba(0,0,0,0.12)] 
transition-all duration-200"
      >
        <div className="aspect-[4/3]  overflow-hidden bg-divider flex-shrink-0">
          <ImageCarousel
            images={project.images?.map((img) => ({
              imageUrl: img,
              title: project?.name,
            }))}
            smallDots
          />
        </div>
        {/* CONTENT */}
        <div
          onClick={() => navigate(`/admin/projects/${project._id}`)}
          className="p-4 flex-1 flex flex-col gap-1 cursor-pointer"
        >
          <h3 className="text-sm font-semibold text-headingText leading-snug line-clamp-1">
            {project?.name}
          </h3>
          <p className="text-xs text-descriptionText line-clamp-2 leading-relaxed">
            {project?.description}
          </p>
        </div>

        {/* HOVER ACTION OVERLAY */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-end gap-3 h-12 px-3 bg-gradient-to-l from-black/50 via-black/10 to-transparent border-t border-white/10 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-300 ease-in-out">
          {/* LEFT SIDE ACTIONS */}
          <button
            title="Edit"
            onClick={() => onEdit(project?._id)}
            className="flex items-center justify-center w-8 h-8 bg-white/20 hover:bg-white/30 text-white border border-white/20 transition"
          >
            <FiEdit size={14} />
          </button>

          <button
            title="Delete"
            onClick={() => onDelete(project)}
            className="flex items-center justify-center w-8 h-8 bg-red-500/70 hover:bg-red-500 text-white border border-red-400/40 transition"
          >
            <FiTrash2 size={14} />
          </button>

          {/* RIGHT SIDE VIEW BUTTON */}
          {/* <button
            title="View Details"
            onClick={() => navigate(`/admin/products/${product._id}`)}
            className="flex items-center justify-center w-8 h-8 bg-primaryAccent/80 hover:bg-primaryAccent text-white border border-primaryAccent/40 transition"
          >
            <FiEye size={14} />
          </button> */}
        </div>
      </div>
    </>
  );
};

/*  Main page */
const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);

  const limit = 8;
  const debouncedSearch = useDebounce(searchInput, 350);
  const projectModal = useEntityModal();
  const abortRef = useRef(null);

  const fetchProjects = useCallback(async () => {
    // Cancel previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      const res = await api.get(
        `/projects?admin=true&page=${page}&limit=${limit}&search=${debouncedSearch}`,
        { signal: controller.signal },
      );
      const { projects, pagination } = res.data.data;
      setProjects(projects);
      setPagination(pagination);
    } catch (error) {
      if (error.name !== "CanceledError" && error.name !== "AbortError") {
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchProjects();
    return () => abortRef.current?.abort();
  }, [fetchProjects]);

  // Reset to page 1 whenever search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleEdit = useCallback((projectId) => {
    projectModal.openEdit(projectId);
  }, []);

  const handleProjectUpdate = useCallback((updatedProject) => {
    setProjects((prev) =>
      prev.map((p) =>
        p._id === updatedProject._id ? { ...p, ...updatedProject } : p,
      ),
    );
  }, []);

  const handleDelete = useCallback(
    async (project) => {
      const confirmed = await confirmAction({
        title: "Delete Project?",
        text: `This will permanently delete "${project.name}".`,
        confirmText: "Delete",
        cancelText: "Cancel",
      });

      if (!confirmed) return;

      const previousProjects = projects;

      try {
        /* OPTIMISTIC UPDATE */
        setProjects((prev) => prev.filter((p) => p._id !== project._id));

        if (pagination?.total) {
          setPagination((prev) => ({
            ...prev,
            total: prev.total - 1,
          }));
        }

        /* API CALL */
        await api.delete(`/projects/${project._id}`);

        showSuccess("Project deleted successfully");
      } catch (error) {
        console.error(error);

        setProjects(previousProjects);

        showError("Failed to delete project");
      }
    },
    [projects, pagination],
  );

  const total = pagination?.total ?? null;

  return (
    <div className="p-6">
      {/* PAGE HEADER */}
      <PageHeader
        title="Projects"
        breadcrumb="Dashboard / Projects"
        customSection={
          <>
            {total != null && (
              <span className="text-xs font-medium text-descriptionText border border-borderLight px-2.5 py-1 bg-surfaceColor">
                {total} {total === 1 ? "project" : "projects"}
              </span>
            )}
          </>
        }
      />
      {/* ACTION BAR */}
      <div className="flex items-center justify-between mb-6 gap-3">
        {/* SEARCH */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-descriptionText pointer-events-none">
            <IoIosSearch className="size-4" />
          </span>
          <input
            type="text"
            placeholder="Search projects…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-[42px] w-[25rem] pl-9 pr-4 border border-borderLight outline-none focus:border-primaryAccent text-sm bg-white transition-colors"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-descriptionText hover:text-headingText text-xs"
            >
              ✕
            </button>
          )}
        </div>

        {/* RIGHT CONTROLS */}
        <div className="flex items-center gap-2">
          <button
            onClick={projectModal.openCreate}
            className="h-[42px] px-5 bg-btnColor text-white text-sm  font-medium  tracking-wide hover:bg-btnHoverColor transition-colors"
          >
            + Create Project
          </button>
        </div>
      </div>
      {/* GRID or SKELETON */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {loading ? (
          Array.from({ length: limit }).map((_, i) => <SkeletonCard key={i} />)
        ) : !loading && projects?.length === 0 ? (
          <EmptyState search={debouncedSearch} />
        ) : (
          projects?.map((project) => (
            <ProjectCard
              key={project._id}
              project={project}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>
      {/* PAGINATION */}
      {!loading && (
        <Pagination
          pagination={pagination}
          page={page}
          onPageChange={setPage}
        />
      )}
      <EditProjectModal
        modal={projectModal}
        onProjectupdated={handleProjectUpdate}
      />{" "}
    </div>
  );
};

/* Empty State */
const EmptyState = ({ search }) => (
  <div className="col-span-full flex items-center justify-center py-24">
    <div className="max-w-sm w-full border border-dashed border-borderLight bg-surfaceAccentLight/40 rounded-xl p-10 flex flex-col items-center text-center">
      {/* ICON */}
      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primaryAccent/10 text-primaryAccent mb-4">
        <PackageSearch size={28} />
      </div>

      {/* TITLE */}
      <p className="text-lg font-semibold text-headingText">
        No projects found
      </p>

      {/* DESCRIPTION */}
      <p className="text-sm text-descriptionText mt-2 leading-relaxed">
        {search
          ? `No results for "${search}". Try using a different keyword.`
          : "You haven't added any products yet. Start by creating your first product."}
      </p>

      {/* ACTION (only when empty without search) */}
      {!search && (
        <button
          className="mt-5 px-5 py-2 bg-btnColor
           text-white text-sm font-medium rounded-md hover:bg-btnHoverColor transition"
          onClick={() =>
            document.dispatchEvent(new CustomEvent("create-project"))
          }
        >
          + Create Project
        </button>
      )}
    </div>
  </div>
);

/*  Skeleton card */
const SkeletonCard = () => (
  <div className="border border-borderLight bg-surfaceColor overflow-hidden animate-pulse">
    <div className="aspect-[4/3] bg-divider" />
    <div className="p-4 space-y-2">
      <div className="h-3 bg-divider rounded w-3/4" />
      <div className="h-3 bg-divider rounded w-full" />
      <div className="h-3 bg-divider rounded w-1/2" />
    </div>
  </div>
);

export default AdminProjects;
